import { inject, type ObjectGraph } from '@jet/environment/dependencies';
import { getBaseSeoData } from './BaseSeoData';
import type { SeoData } from './types';
import type { DetailPage } from '@jet-app/music-classical/models/pages/detail';
import type { EditorialNotes } from '@jet-app/music-classical/api/types';
import { ObjectGraphType } from '../../dependencies';
import {
    ACTION_SCHEMA_TYPE,
    buildLinkUrl,
    buildSrc,
    createActionSchema,
} from './utils';
import type { Opt } from '@jet/environment/types/optional';

interface ByArtistSchema {
    '@type': string;
    name: string;
}

interface RecordingSchema {
    '@type': string;
    image?: string;
    url?: string;
    name: string;
    byArtist: ByArtistSchema;
}

/**
 * @method getWorkSeoData
 * @param {string} url Detail canonical URL
 * @param {DetailPage} page Detail page object
 * @param {ObjectGraph} objectGraph
 * @returns {SeoData}
 */
export function getWorkSeoData(
    url: string,
    page: DetailPage,
    objectGraph: ObjectGraph,
): SeoData {
    const localization = inject(ObjectGraphType.localization, objectGraph);
    const baseSeoData: SeoData = getBaseSeoData(url, objectGraph);
    const work: string = page.header.title;
    const catalogNumber: string = page.header.subtitle;
    const composer: string = page.header.composerName;
    const editorialNotes: EditorialNotes = page.header.editorialNotes;
    const workDescription: string =
        editorialNotes?.short ??
        editorialNotes?.standard ??
        editorialNotes?.title;
    const artworkObj = page.header.artwork;
    const recordings = page.sections.find(
        (section) => section.id === 'list_recording',
    )?.items;

    function getPageTitle() {
        if (catalogNumber) {
            return localization.string(
                'ASE.Web.MusicClassicalClientJS.Meta.Work.Title.WithCatalogNumber',
                {
                    work,
                    composer,
                    catalogNumber,
                },
            );
        }

        return localization.string(
            'ASE.Web.MusicClassicalClientJS.Meta.Work.Title',
            {
                work,
                composer,
            },
        );
    }

    function getPageDescription() {
        if (workDescription) {
            return workDescription.slice(0, 160);
        }

        return localization.string(
            'ASE.Web.MusicClassicalClientJS.Meta.Work.Description',
            {
                work,
                composer,
            },
        );
    }

    const pageTitle: string = getPageTitle();
    const description: string = getPageDescription();

    /**
     * Generates the structured metadata object for a recording reference
     *
     * @method _schemaDataForRecording
     * @param {any} recording
     * @returns {Opt<RecordingSchema>}
     * @protected
     */
    function _schemaDataForRecording(recording: any): Opt<RecordingSchema> {
        const name: Opt<string> = recording.action?.title;
        const artworkURL: Opt<string> = recording.artwork?.template;
        const artists: Opt<string> = recording.action?.header?.artists;

        if (name) {
            let image: Opt<string>;

            if (artworkURL) {
                image = buildSrc(artworkURL, {
                    width: 1200,
                    height: 1200,
                    crop: 'bb',
                    fileType: 'webp',
                });
            }

            return {
                '@type': 'MusicRecording',
                image,
                url: buildLinkUrl(recording.url),
                name,
                byArtist: !!artists
                    ? {
                          '@type': 'MusicGroup',
                          name: artists,
                      }
                    : undefined,
            };
        }

        return undefined;
    }

    /**
     *
     * Generate SEO data object
     *
     */
    return {
        ...baseSeoData,
        pageTitle,
        description,

        /**
         * Social OG Tags
         */
        socialTitle: pageTitle,
        socialDescription: description,

        /**
         *
         * Work Apple-spcific Values
         *
         */
        appleContentId: undefined,
        appleTitle: pageTitle,
        appleDescription: description,

        /**
         *
         * Work Artwork Values
         *
         */
        artworkUrl: artworkObj?.template ?? baseSeoData.artworkUrl,

        /**
         *
         * Work Structured Data/schema.org Values
         *
         */
        schemaName: 'schema:work',
        schemaContent: {
            '@context': 'http://schema.org',
            '@type': 'MusicComposition',
            name: work,
            composer: {
                '@type': 'Person',
                name: composer,
            },
            description: workDescription || description,
            url,
            potentialAction: createActionSchema(url, ACTION_SCHEMA_TYPE.View),
            recordedAs: recordings?.map((recording) => {
                return _schemaDataForRecording(recording);
            }),
        },
    };
}

export function addingWorkSeoData(
    url: string,
    page: DetailPage,
    objectGraph: ObjectGraph,
): DetailPage {
    return {
        ...page,
        seoData: getWorkSeoData(url, page, objectGraph),
    };
}
